import { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import StandaloneBranchSetup from './StandaloneBranchSetup';
import BranchList from './BranchList';
import { SelectedCompanyContext } from '../contexts/SelectedCompanyContext';
import SmartAddressInput from './journey-wizard/components/SmartAddressInput';
import { 
  AnimatedBackground, 
  ModernCard, 
  ModernButton, 
  ModernStatCard,
  ModernInput,
  ModernSelect,
  ModernProgressRing 
} from './ui/modern';

function CompanyDetails({ user }) {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [previewProgress, setPreviewProgress] = useState(null);
  const navigate = useNavigate();
  const isManager = user?.role === 'Manager';
  const isAdmin = user?.role === 'Admin';
  const canEditDelete = isAdmin || isManager;
  const { setSelected } = useContext(SelectedCompanyContext);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnicianIds, setSelectedTechnicianIds] = useState([]);
  const [sectors, setSectors] = useState([]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await api.get(`/companies/${companyId}`);
        setCompany(response.data);
        setFormData(response.data);
        setSelected(response.data);
        // Set initially assigned technician IDs
        const assignedIds = response.data.Technicians?.map(t => t.id) || [];
        setSelectedTechnicianIds(assignedIds);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching company details:', error);
        setLoading(false);
      }
    };
    fetchCompany();
  }, [companyId, setSelected]);

  useEffect(() => {
    let cancelled = false;
    async function fetchPreview() {
      try {
        const res = await api.get(`/reports/company/${companyId}/preview`);
        if (!cancelled) setPreviewProgress(res.data?.progress || null);
      } catch (e) {
        console.error('Error fetching onboarding preview:', e);
        if (!cancelled) setPreviewProgress(null);
      }
    }
    fetchPreview();
    return () => { cancelled = true; };
  }, [companyId]);

  // Fetch available assignable users for assignment
  useEffect(() => {
    if (canEditDelete) {
      api.get('/users/assignable')
        .then(res => setTechnicians(res.data))
        .catch(err => console.error('Error fetching assignable users:', err));
    }
  }, [canEditDelete]);

  // Fetch sectors for admin-only sector selection
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    async function fetchSectors() {
      try {
        const res = await api.get('/sectors');
        if (!cancelled) setSectors(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Error fetching sectors:', e);
      }
    }
    fetchSectors();
    return () => { cancelled = true; };
  }, [isAdmin]);

  const handleTechnicianToggle = (techId) => {
    setSelectedTechnicianIds(prev => 
      prev.includes(techId)
        ? prev.filter(id => id !== techId)
        : [...prev, techId]
    );
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleNumberInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: parseInt(value) || 0 });
  };

  // Handle address selection from SmartAddressInput
  const handleAddressSelect = (addressData) => {
    setFormData({
      ...formData,
      address: addressData.address,
      city: addressData.city,
      province: addressData.province,
      postalCode: addressData.postalCode,
      country: addressData.country || 'South Africa',
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // Include technician assignments in the update
      const updateData = {
        ...formData,
        technicianIds: selectedTechnicianIds
      };
      const response = await api.put(`/companies/${companyId}`, updateData);
      setCompany(response.data);
      setEditMode(false);
      alert('Company updated successfully!');
    } catch (error) {
      console.error('Error updating company:', error);
      alert('Error updating company. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this company? This action cannot be undone and will delete all associated data.')) {
      try {
        await api.delete(`/companies/${companyId}`);
        alert('Company deleted successfully!');
        setSelected(null);
        navigate('/companies', { replace: true });
      } catch (error) {
        console.error('Error deleting company:', error);
        alert('Error deleting company. Please try again.');
      }
    }
  };

  const handleITGlueSync = async () => {
    if (!window.confirm('This will create/update the organization and all related data in IT Glue. Continue?')) {
      return;
    }
    
    setSyncing(true);
    try {
      const response = await api.post(`/companies/${companyId}/sync-itglue`);
      
      if (response.data.success) {
        const results = response.data.results;
        const summary = [
          `✓ Organization synced`,
          `✓ ${results.locations.count} locations created`,
          `✓ ${results.contacts.count} contacts created`,
          `✓ ${results.configurations.count} configurations created`,
          `✓ ${results.passwords.count} passwords stored`,
        ];
        
        // Show errors/warnings if any
        if (response.data.errors && response.data.errors.length > 0) {
          summary.push(`\n⚠️ Warnings (${response.data.errors.length}):`);
          response.data.errors.forEach((error, index) => {
            summary.push(`  ${index + 1}. ${error}`);
          });
        }
        
        // Show if items were skipped
        if (results.locations.total > results.locations.count) {
          summary.push(`\n⚠️ ${results.locations.total - results.locations.count} locations failed to sync`);
        }
        if (results.contacts.total > results.contacts.count) {
          summary.push(`⚠️ ${results.contacts.total - results.contacts.count} contacts failed to sync`);
        }
        if (results.configurations.total > results.configurations.count) {
          summary.push(`⚠️ ${results.configurations.total - results.configurations.count} configurations failed to sync`);
        }
        if (results.passwords.total > results.passwords.count) {
          summary.push(`⚠️ ${results.passwords.total - results.passwords.count} passwords failed to sync`);
        }
        
        alert('Successfully synced to IT Glue!\n\n' + summary.join('\n'));
        
        // Refresh company data to show updated sync status
        const updated = await api.get(`/companies/${companyId}`);
        setCompany(updated.data);
        setFormData(updated.data);
      } else {
        alert(`Sync failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('IT Glue sync error:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Sync failed: ${errorMsg}`);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white text-lg">Loading company details...</div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white text-lg">Company not found.</div>
      </div>
    );
  }

  // Calculate stats
  const branchCount = company.Branches?.length || 0;
  const taskCount = company.CompanyTasks?.length || 0;
  const completedTasks = company.CompanyTasks?.filter(t => t.status === 'Completed')?.length || 0;
  const totalUsers = company.Branches?.reduce((sum, b) => sum + (b.numberOfUsers || 0), 0) || 0;
  const completionRate = typeof previewProgress?.percentage === 'number'
    ? previewProgress.percentage
    : (taskCount > 0 ? Math.round((completedTasks / taskCount) * 100) : 0);
  const previewTotal = typeof previewProgress?.total === 'number' ? previewProgress.total : null;
  const previewCompleted = typeof previewProgress?.completed === 'number' ? previewProgress.completed : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-x-hidden">
      <AnimatedBackground />

      <div className="relative z-10 px-3 py-4 sm:px-6 lg:px-8 sm:py-6 max-w-7xl mx-auto w-full pb-safe overflow-x-hidden">
        {/* Header Card - Mobile Optimized */}
        <ModernCard className="mb-4 sm:mb-8">
          {/* Mobile Layout */}
          <div className="block sm:hidden space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-violet-500 rounded-xl flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
                {company.name?.charAt(0) || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl font-bold text-white mb-0.5 truncate">{company.name}</h1>
                <p className="text-slate-400 text-sm truncate">
                  {company.registrationNumber || 'No registration number'}
                </p>
              </div>
            </div>
            {canEditDelete && (
              <div className="flex flex-col gap-2">
                <ModernButton
                  variant="secondary"
                  onClick={() => navigate('/companies')}
                  className="w-full justify-center"
                >
                  ← Back
                </ModernButton>
                <div className="grid grid-cols-2 gap-2">
                  <ModernButton
                    variant={editMode ? 'secondary' : 'primary'}
                    onClick={handleEditToggle}
                    className="justify-center text-sm"
                  >
                    {editMode ? 'Cancel' : 'Edit'}
                  </ModernButton>
                  <ModernButton
                    variant="danger"
                    onClick={handleDelete}
                    className="justify-center text-sm"
                  >
                    Delete
                  </ModernButton>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Layout */}
          <div className="hidden sm:flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Company Logo/Initial */}
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-violet-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
                {company.name?.charAt(0) || '?'}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">{company.name}</h1>
                <p className="text-slate-400">
                  {company.registrationNumber || 'No registration number'}
                </p>
              </div>
            </div>

            {canEditDelete && (
              <div className="flex items-center gap-3">
                <ModernButton
                  variant="secondary"
                  onClick={() => navigate('/companies')}
                >
                  ← Back to Companies
                </ModernButton>
                <ModernButton
                  variant={editMode ? 'secondary' : 'primary'}
                  onClick={handleEditToggle}
                >
                  {editMode ? 'Cancel Edit' : 'Edit Company'}
                </ModernButton>
                <ModernButton
                  variant="danger"
                  onClick={handleDelete}
                >
                  Delete
                </ModernButton>
              </div>
            )}
          </div>
        </ModernCard>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-8 w-full">
          <ModernStatCard
            title="Branches"
            value={branchCount}
            icon="🏢"
            color="blue"
          />
          <ModernStatCard
            title="Total Tasks"
            value={taskCount}
            icon="📋"
            color="violet"
          />
          <ModernStatCard
            title="Completed"
            value={completedTasks}
            icon="✓"
            color="emerald"
          />
          <ModernStatCard
            title="Total Users"
            value={totalUsers}
            icon="👥"
            color="amber"
          />
        </div>

        {/* Onboarding Action Button */}
        {(previewTotal ?? taskCount) > 0 ? (
          <ModernCard className="mb-8 bg-gradient-to-r from-blue-600/10 to-violet-600/10 border-blue-500/30">
            <div>
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <span>🚀</span>
                Onboarding Progress
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                {completionRate === 100 
                  ? 'All onboarding tasks completed!' 
                  : `${(previewCompleted ?? completedTasks)} of ${(previewTotal ?? taskCount)} tasks completed (${completionRate}%)`}
              </p>
              {/* Progress Bar */}
              <div className="mb-4 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all duration-500"
                  style={{ width: `${completionRate}%` }}
                />
              </div>
              {/* Button at bottom */}
              <ModernButton
                onClick={() => navigate(`/onboarding/${companyId}`)}
                variant="primary"
                className="w-full"
              >
                {completionRate === 100 ? 'View Onboarding' : 'Continue Onboarding'} →
              </ModernButton>
            </div>
          </ModernCard>
        ) : (
          <ModernCard className="mb-8 bg-gradient-to-r from-emerald-600/10 to-teal-600/10 border-emerald-500/30">
            <div>
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <span>✨</span>
                Start Onboarding
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                This company hasn't been onboarded yet. Start the onboarding wizard to select systems and configure services.
              </p>
              {/* Button at bottom */}
              <ModernButton
                onClick={() => navigate(`/onboarding/${companyId}/wizard`)}
                variant="success"
                className="w-full"
              >
                🚀 Start Onboarding
              </ModernButton>
            </div>
          </ModernCard>
        )}

        {/* Main Content */}
        {editMode ? (
          /* Edit Mode Form */
          <ModernCard className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">Edit Company</h2>
            <form onSubmit={handleUpdate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Company Details */}
                <ModernInput
                  label="Company Name"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  required
                />
                <ModernInput
                  label="Registration Number"
                  name="registrationNumber"
                  value={formData.registrationNumber || ''}
                  onChange={handleInputChange}
                />
                {isAdmin && (
                  <ModernSelect
                    label="Sector"
                    value={formData.sectorId ? String(formData.sectorId) : ''}
                    onChange={(e) => {
                      const next = e.target.value ? Number(e.target.value) : null;
                      setFormData({ ...formData, sectorId: next });
                    }}
                    options={[
                      { value: '', label: 'Default sector' },
                      ...(sectors || []).map((s) => ({
                        value: String(s.id),
                        label: s.isDefault ? `${s.name} (Default)` : s.name,
                      })),
                    ]}
                  />
                )}
                <ModernInput
                  label="VAT Number"
                  name="vatNumber"
                  value={formData.vatNumber || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Number of Branches"
                  name="numberOfBranches"
                  type="number"
                  value={formData.numberOfBranches || 0}
                  onChange={handleNumberInputChange}
                />

                {/* Address - Smart Address Input with Autocomplete */}
                <div className="col-span-2">
                  <SmartAddressInput
                    value={formData.address || ''}
                    onChange={(value) => setFormData({ ...formData, address: value })}
                    onSelect={handleAddressSelect}
                    placeholder="Start typing an address..."
                  />
                </div>
                <ModernInput
                  label="City"
                  name="city"
                  value={formData.city || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Province"
                  name="province"
                  value={formData.province || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Postal Code"
                  name="postalCode"
                  value={formData.postalCode || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Country"
                  name="country"
                  value={formData.country || ''}
                  onChange={handleInputChange}
                />

                {/* Contact Information */}
                <ModernInput
                  label="Contact Person"
                  name="contactPerson"
                  value={formData.contactPerson || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Contact Number"
                  name="contactNumber"
                  type="tel"
                  value={formData.contactNumber || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Email Address"
                  name="emailAddress"
                  type="email"
                  value={formData.emailAddress || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Secondary Contact"
                  name="secondaryContact"
                  value={formData.secondaryContact || ''}
                  onChange={handleInputChange}
                />

                {/* Billing Information */}
                <ModernInput
                  label="Billing Contact Name"
                  name="billingContactName"
                  value={formData.billingContactName || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Billing Email"
                  name="billingEmail"
                  type="email"
                  value={formData.billingEmail || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Billing Address"
                  name="billingAddress"
                  value={formData.billingAddress || ''}
                  onChange={handleInputChange}
                />
                <ModernSelect
                  label="Payment Terms"
                  name="paymentTerms"
                  value={formData.paymentTerms || '7 Days'}
                  onChange={handleInputChange}
                  options={[
                    { value: '7 Days', label: '7 Days' },
                    { value: '14 Days', label: '14 Days' },
                    { value: '30 Days', label: '30 Days' },
                    { value: 'Month End', label: 'Month End' },
                  ]}
                />
              </div>

              {/* Team Assignment Section */}
              {technicians.length > 0 && (
                <div className="mt-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <span>👥</span>
                    Assign Team Members
                  </h3>
                  <p className="text-sm text-slate-400 mb-4">
                    Select team members who can access and work on this company's onboarding.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {technicians.map(tech => {
                      const getRoleBadgeColor = (role) => {
                        switch (role) {
                          case 'Admin':
                            return 'bg-red-500/20 border-red-500/30 text-red-300';
                          case 'Manager':
                            return 'bg-blue-500/20 border-blue-500/30 text-blue-300';
                          case 'Technician':
                            return 'bg-green-500/20 border-green-500/30 text-green-300';
                          default:
                            return 'bg-gray-500/20 border-gray-500/30 text-gray-300';
                        }
                      };
                      
                      return (
                        <label
                          key={tech.id}
                          className={`flex flex-col gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedTechnicianIds.includes(tech.id)
                              ? 'bg-blue-500/20 border-blue-500 text-white'
                              : 'bg-slate-700/30 border-slate-600 text-slate-300 hover:bg-slate-700/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedTechnicianIds.includes(tech.id)}
                              onChange={() => handleTechnicianToggle(tech.id)}
                              className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-blue-500 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium truncate flex-1">{tech.name}</span>
                            {selectedTechnicianIds.includes(tech.id) && (
                              <span className="text-blue-400">✓</span>
                            )}
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getRoleBadgeColor(tech.role)}`}>
                            {tech.role}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {selectedTechnicianIds.length > 0 && (
                    <p className="mt-3 text-xs text-slate-400">
                      {selectedTechnicianIds.length} team {selectedTechnicianIds.length === 1 ? 'member' : 'members'} assigned
                    </p>
                  )}
                </div>
              )}

              <div className="mt-8 flex gap-3">
                <ModernButton type="submit" variant="primary" className="flex-1">
                  Update Company
                </ModernButton>
                <ModernButton type="button" variant="secondary" onClick={handleEditToggle}>
                  Cancel
                </ModernButton>
              </div>
            </form>
          </ModernCard>
        ) : (
          /* View Mode */
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Company Information */}
            <ModernCard>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span>🏢</span>
                Company Information
              </h3>
              <div className="space-y-3">
                <InfoRow label="Registration Number" value={company.registrationNumber} />
                <InfoRow label="VAT Number" value={company.vatNumber} />
                <InfoRow 
                  label="Address" 
                  value={[company.address, company.city, company.province, company.postalCode, company.country].filter(Boolean).join(', ')} 
                />
                <InfoRow label="Number of Branches" value={company.numberOfBranches} />
              </div>
            </ModernCard>

            {/* Contact Information */}
            <ModernCard>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span>📞</span>
                Contact Information
              </h3>
              <div className="space-y-3">
                <InfoRow label="Contact Person" value={company.contactPerson} />
                <InfoRow label="Contact Number" value={company.contactNumber} />
                <InfoRow label="Email Address" value={company.emailAddress} />
                <InfoRow label="Secondary Contact" value={company.secondaryContact} />
              </div>
            </ModernCard>

            {/* Billing Information */}
            <ModernCard>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span>💳</span>
                Billing Information
              </h3>
              <div className="space-y-3">
                <InfoRow label="Billing Contact" value={company.billingContactName} />
                <InfoRow label="Billing Email" value={company.billingEmail} />
                <InfoRow label="Billing Address" value={company.billingAddress} />
                <InfoRow label="Payment Terms" value={company.paymentTerms} />
              </div>
            </ModernCard>

            {/* Progress Overview */}
            <ModernCard>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span>📊</span>
                Progress Overview
              </h3>
              <div className="flex items-center justify-center py-4">
                <ModernProgressRing 
                  percentage={completionRate}
                  size={140}
                  strokeWidth={10}
                />
              </div>
              <div className="mt-4 text-center text-slate-400 text-sm">
                {(previewCompleted ?? completedTasks)} of {(previewTotal ?? taskCount)} tasks completed
              </div>
            </ModernCard>

            {/* Assigned Team Members */}
            {canEditDelete && (
              <ModernCard className="lg:col-span-2">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <span>👥</span>
                  Assigned Team Members
                </h3>
                {company.Technicians && company.Technicians.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {company.Technicians.map(tech => {
                      const getRoleBadgeColor = (role) => {
                        switch (role) {
                          case 'Admin':
                            return 'bg-red-500/20 border-red-500/30 text-red-300';
                          case 'Manager':
                            return 'bg-blue-500/20 border-blue-500/30 text-blue-300';
                          case 'Technician':
                            return 'bg-green-500/20 border-green-500/30 text-green-300';
                          default:
                            return 'bg-gray-500/20 border-gray-500/30 text-gray-300';
                        }
                      };
                      
                      return (
                        <span 
                          key={tech.id}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 border rounded-full text-sm ${getRoleBadgeColor(tech.role || 'Technician')}`}
                        >
                          <span className="w-2 h-2 bg-current rounded-full"></span>
                          {tech.name}
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm">
                    No team members assigned. Click "Edit Company" to assign team members.
                  </p>
                )}
              </ModernCard>
            )}
          </div>
        )}

        {/* IT Glue Integration Section */}
        {canEditDelete && !editMode && (
          <ModernCard className="mb-8">
            <div>
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <span>🔗</span>
                IT Glue Integration
              </h3>
              <div className="space-y-1 mb-4">
                {company.itGlueSyncedAt ? (
                  <>
                    <p className="text-sm text-emerald-400 flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                      Last synced: {new Date(company.itGlueSyncedAt).toLocaleString()}
                    </p>
                    {company.itGlueOrgId && (
                      <p className="text-xs text-slate-400">
                        Organization ID: {company.itGlueOrgId}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-400 flex items-center gap-2">
                    <span className="w-2 h-2 bg-slate-500 rounded-full"></span>
                    Not yet synced to IT Glue
                  </p>
                )}
                
                {company.itGlueSyncStatus === 'failed' && company.itGlueSyncError && (
                  <p className="text-sm text-red-400 mt-2 p-2 bg-red-500/10 rounded border border-red-500/20">
                    ⚠️ Error: {company.itGlueSyncError}
                  </p>
                )}
                
                {company.itGlueSyncStatus === 'syncing' && (
                  <p className="text-sm text-blue-400 flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></span>
                    Sync in progress...
                  </p>
                )}
              </div>
              
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <p className="text-xs text-slate-500 mb-4">
                  This will sync company details, branches, contacts, devices, servers, and credentials to IT Glue.
                </p>
                {/* Button at bottom */}
                <ModernButton
                  onClick={handleITGlueSync}
                  disabled={syncing || company.itGlueSyncStatus === 'syncing'}
                  variant={company.itGlueSyncedAt ? 'secondary' : 'primary'}
                  className="w-full"
                >
                  {syncing ? (
                    <>
                      <span className="inline-block animate-spin mr-2">⟳</span>
                      Syncing...
                    </>
                  ) : company.itGlueSyncedAt ? (
                    '🔄 Resync to IT Glue'
                  ) : (
                    '📤 Push to IT Glue'
                  )}
                </ModernButton>
              </div>
            </div>
          </ModernCard>
        )}

        {/* Branches Section */}
        {(isManager || isAdmin) && (
          <ModernCard>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2 mb-4">
              <span>🏢</span>
              Branches
            </h2>
            <ModernButton
              variant={showBranchForm ? 'secondary' : 'primary'}
              onClick={() => setShowBranchForm(!showBranchForm)}
              className="mb-6"
            >
              {showBranchForm ? 'Close Form' : '+ Add Branch'}
            </ModernButton>
            
            {showBranchForm && (
              <div className="mb-6">
                <StandaloneBranchSetup 
                  company={company}
                  companyId={companyId} 
                  onSuccess={() => { 
                    setShowBranchForm(false); 
                    setRefreshCounter(c => c + 1); 
                  }}
                />
              </div>
            )}
            
            <BranchList companyId={companyId} refreshSignal={refreshCounter} />
          </ModernCard>
        )}
      </div>
    </div>
  );
}

// Helper component for info rows
const InfoRow = ({ label, value }) => (
  <div className="flex justify-between items-start gap-4 py-2 border-b border-white/5">
    <span className="text-slate-400 text-sm font-medium">{label}</span>
    <span className="text-white text-sm text-right">{value || 'N/A'}</span>
  </div>
);

export default CompanyDetails;
